import Meta from '../components/Meta'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  GetAccountsDocument,
  GetAccountsQuery,
  GetAccountsQueryVariables,
  useDeleteAccountMutation,
} from '../generated/urql.user'
import { useClient } from 'urql'
import { Button, ButtonGroup } from 'react-bootstrap'
import { PencilSquare, PersonPlus, Trash } from 'react-bootstrap-icons'
import { useNavigate } from 'react-router'
import { InfiniteTable } from '../components/InfiniteTable'

const BatchSize = 100

const Accounts: FC = () => {
  // page content
  const pageTitle = 'Konta'
  const [accounts, setAccounts] = useState<GetAccountsQuery['accounts']>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const client = useClient()
  const navigate = useNavigate()
  const data = useMemo(() => accounts, [accounts])
  const [, deleteAccount] = useDeleteAccountMutation()

  const loadMoreRows = useCallback(
    async ({ refetch = false }: { refetch: boolean } = { refetch: false }) => {
      const { data, error } = await client
        .query<GetAccountsQuery, GetAccountsQueryVariables>(
          GetAccountsDocument,
          { offset: refetch ? 0 : offset, limit: BatchSize },
          { requestPolicy: refetch ? 'network-only' : undefined }
        )
        .toPromise()

      if (error) {
        console.error(error)
        return
      }

      if (data) {
        setAccounts(refetch ? data.accounts : accounts.concat(data.accounts))
        setOffset(refetch ? BatchSize : offset + BatchSize)
        setCount(data.accountsAggregate.aggregate?.count || 0)
      }
    },
    [client, accounts, offset]
  )

  useEffect(() => {
    loadMoreRows({ refetch: true }).then(() => {})
  }, [])

  return (
    <div>
      <Meta title={pageTitle} />
      <div className="mt-3 mb-3 d-flex justify-content-end">
        <ButtonGroup>
          <Button
            className="d-flex align-items-center"
            onClick={() => navigate('/account')}
          >
            <PersonPlus size={20} />
            &nbsp;Dodaj
          </Button>
        </ButtonGroup>
      </div>
      <InfiniteTable<GetAccountsQuery['accounts'][number]>
        data={data}
        columns={[
          {
            Header: 'Id',
            accessor: ({ id }) => (
              <p className="mb-1 small text-secondary">{id}</p>
            ),
          },
          { Header: 'Email', accessor: 'email' },
          {
            Header: 'Sklepy',
            accessor: (rowData) => (
              <ul>
                {rowData.accountStores.map(({ store }) => (
                  <li>{store?.name}</li>
                ))}
              </ul>
            ),
          },
          {
            Header: '',
            id: 'actions',
            accessor: (rowData) => (
              <div className="d-flex justify-content-end">
                <ButtonGroup>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/account/${rowData.id}`)}
                  >
                    <PencilSquare size={18} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={async () => {
                      if (window.confirm('Czy napewno chcesz usunąc konto?')) {
                        try {
                          await deleteAccount({ id: rowData.id })
                        } finally {
                          await loadMoreRows({ refetch: true })
                        }
                      }
                    }}
                  >
                    <Trash size={18} />
                  </Button>
                </ButtonGroup>
              </div>
            ),
          },
        ]}
        loadMoreRows={loadMoreRows}
        hasMore={offset < count}
      />
    </div>
  )
}

export default Accounts
